import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignatureCanvas } from "@/components/crm/SignatureCanvas";
import { MesContratsSection } from "@/components/crm/MesContratsSection";
import { MesEvaluationsSection } from "@/components/employe/MesEvaluationsSection";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { useLocation, useNavigate } from "react-router-dom";
import { TableauBordTerrain } from "@/components/TableauBordTerrain";
import { BTsEnRenfort } from "@/components/BTsEnRenfort";  // 🆕 2026-05-07 BTs en renfort pour CP/adjoint
import { toast } from "sonner";
import api from "@/lib/api";
import { versementLabel } from "@/lib/versementLabel";
import {
  User, Clock, DollarSign, ShirtIcon, FileSignature, Trophy, AlertTriangle,
  CheckCircle, Loader2, ChevronRight, Calendar, TrendingUp, Umbrella, Plus,
  MapPin, Briefcase, ChevronLeft, CalendarDays, HardHat, Gift, Star, Key, BarChart2,
  Mail, Phone, ShieldCheck, Package, MonitorSmartphone
} from "lucide-react";
import { ListeAppareils } from "@/components/securite/Appareils"; // 🔐 Connexion P4

// QuickPunchWidget absorbé dans ChantierDuJour (2026-02)
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { ChantierDuJour } from "@/components/ChantierDuJour";
// 🗺️ [v13.63 Phase 3 — 2026-02-28] Rechercher mon chantier (secours horaire)
import { RechercherMonChantier } from "@/components/RechercherMonChantier";
// 📏 [v13.63 Phase 4 — 2026-02-28] Kilométrage employé + adresse domicile self-service
import { MonKilometrage } from "@/components/MonKilometrage";
import { MonAdresseDomicile } from "@/components/MonAdresseDomicile";
// 📅 [v13.63 Phase 5 — 2026-02-28] Récap kilométrage 7 derniers jours
import { MonKilometrageSemaine } from "@/components/MonKilometrageSemaine";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples
// PlanningSection removed - replaced by CalendrierProductionEmploye

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// 🆕 Composant Accordéon réutilisable
function AccordionSection({ icon: Icon, title, badge, children, defaultOpen = false, headerAction = null }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-slate-200 rounded-lg mb-3 overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
            {badge && <span className="text-xs text-slate-500">{badge}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {headerAction && (
            <div onClick={(e) => e.stopPropagation()}>
              {headerAction}
            </div>
          )}
          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </div>
      </div>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
}

const VIEW_LABELS = { semaine: "Semaine", periode: "Période de paie", mois: "Mois" };

const JOURS_FR = ["D", "L", "M", "M", "J", "V", "S"];
const MOIS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];

// 🆕 [2026-06-11] Tag discret "Hors zone — Xm" sur les punchs passés.
// - Vise un affichage purement informatif (PAS d'alerte, PAS de blocage).
// - position = 'in' | 'out' | 'any' (any = prend la plus grande distance disponible).
// 🔧 [2026-06-11 hotfix] : le champ DB pour le punch-OUT est `geo_check` (legacy).
// 🆕 [2026-06-11 v2] : suffixe « Indulgence » ou « Avertissement officiel » selon
//   le statut du pending_warning associé (admin l'a déjà classé via ValidationHeures).
//   - statut 'indulge'                       → « Indulgence »      (gris)
//   - statut 'auto_strike' | 'approuve'      → « Avertissement officiel » (orange)
//   - statut 'en_attente' | null | inconnu   → pas de suffixe (badge gris discret)
function HorsZoneTag({ punch, position = 'any' }) {
  if (!punch) return null;
  const ci = punch.geo_check_in || {};
  const co = punch.geo_check || punch.geo_check_out || {};

  // Détermine la distance et le statut selon la position demandée.
  let dist = null;
  let statut = null;
  if (position === 'in' && ci.hors_zone === true && typeof ci.distance_metres === 'number') {
    dist = ci.distance_metres;
    statut = punch.pending_warning_statut_in || null;
  } else if (position === 'out' && co.hors_zone === true && typeof co.distance_metres === 'number') {
    dist = co.distance_metres;
    statut = punch.pending_warning_statut_out || null;
  } else if (position === 'any') {
    const inDist = (ci.hors_zone === true && typeof ci.distance_metres === 'number') ? ci.distance_metres : null;
    const outDist = (co.hors_zone === true && typeof co.distance_metres === 'number') ? co.distance_metres : null;
    if (inDist != null && outDist != null) {
      if (outDist >= inDist) { dist = outDist; statut = punch.pending_warning_statut_out || null; }
      else { dist = inDist; statut = punch.pending_warning_statut_in || null; }
    } else if (inDist != null) {
      dist = inDist; statut = punch.pending_warning_statut_in || null;
    } else if (outDist != null) {
      dist = outDist; statut = punch.pending_warning_statut_out || null;
    }
  }
  if (dist == null) return null;
  const m = Math.round(dist);

  // Mapping statut → suffixe + style
  let suffixe = '';
  let className = 'bg-slate-100 text-slate-600';
  let title = 'Pointage enregistré hors de la zone du chantier';
  if (statut === 'indulge') {
    suffixe = ' — Indulgence';
    className = 'bg-slate-100 text-slate-700';
    title = 'Hors zone — indulgence accordée par la direction';
  } else if (statut === 'auto_strike' || statut === 'approuve') {
    suffixe = ' — Avertissement officiel';
    className = 'bg-orange-100 text-orange-700';
    title = 'Hors zone — avertissement officiel à votre dossier';
  }

  return (
    <span
      className={`inline-flex items-center gap-1 ${className} text-[10px] px-1.5 py-0.5 rounded font-normal`}
      title={title}
      data-testid={`hors-zone-tag-${punch.id}-${position}`}
    >
      <MapPin className="w-2.5 h-2.5" />
      Hors zone — {m}&nbsp;m{suffixe}
    </span>
  );
}

// 🆕 Helper : groupe les punchs lavage par (date, route_lavage_id).
// Retourne la liste originale avec les punchs lavage remplacés par des
// "méta-punchs" du type { __lavage_group: true, date, clients: [...] }.
// Les autres punchs (chantier, admin, interne) restent intacts.
function groupLavagePunches(punches) {
  if (!punches || punches.length === 0) return [];
  const groups = {}; // "date|route_id" → group
  const result = [];
  for (const p of punches) {
    if (p.type_punch === 'lavage' && p.route_lavage_id) {
      const date = (p.punch_in_time || '').substring(0, 10);
      const key = `${date}|${p.route_lavage_id}`;
      if (!groups[key]) {
        groups[key] = {
          __lavage_group: true,
          id: `lavage-${key}`,
          date,
          route_lavage_id: p.route_lavage_id,
          clients: [],
          heures_travaillees: 0,
          heures_pause_auto: 0,
          heures_nettes: 0,
          statut: p.statut, // statut dominant (complete ou autre)
          punch_in_time: p.punch_in_time,
          punch_out_time: p.punch_out_time,
        };
        result.push(groups[key]);
      }
      const g = groups[key];
      g.clients.push(p);
      g.heures_travaillees += (p.heures_travaillees || 0);
      g.heures_pause_auto += (p.pause_auto_lavage_journee || 0);
      g.heures_nettes += (p.heures_nettes || 0);
      // Dates min/max pour la journée
      if (!g.punch_in_time || (p.punch_in_time && p.punch_in_time < g.punch_in_time)) g.punch_in_time = p.punch_in_time;
      if (!g.punch_out_time || (p.punch_out_time && p.punch_out_time > g.punch_out_time)) g.punch_out_time = p.punch_out_time;
    } else {
      result.push(p);
    }
  }
  return result;
}

// 🆕 Composant : ligne d'une journée de route de lavage (agrégée)
function LavageJourRow({ group, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(group.punch_in_time || `${group.date}T00:00:00`);
  const totalH = (group.heures_nettes || 0).toFixed(1);
  const pauseMin = Math.round((group.heures_pause_auto || 0) * 60);

  return (
    <div className={`border border-cyan-200 bg-cyan-50/40 rounded ${compact ? 'mx-0 px-2 py-1' : 'p-2'}`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`lavage-jour-${group.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-cyan-700 text-sm">🧽</span>
          <div className="min-w-0">
            <p className={`font-medium text-slate-800 ${compact ? 'text-xs' : 'text-sm'}`}>
              {date.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}
              <span className="ml-1 text-cyan-700">· Route de lavage</span>
            </p>
            <p className={`text-slate-500 ${compact ? 'text-[10px]' : 'text-xs'}`}>
              {group.clients.length} client{group.clients.length > 1 ? 's' : ''}
              {pauseMin > 0 && <span className="text-slate-400 ml-2">· Pause: {pauseMin} min</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`font-bold text-cyan-700 ${compact ? 'text-xs' : 'text-sm'}`}>{totalH}h</span>
          <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-cyan-200 space-y-1">
          {group.clients.map((c, i) => {
            const heureIn = c.punch_in_time ? new Date(c.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
            const heureOut = c.punch_out_time ? new Date(c.punch_out_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
            const nomClient = (c.contrat_nom || '').replace(/^Lavage\s*[—-]\s*/i, '') || `Client ${i + 1}`;
            return (
              <div key={c.id} className="flex items-center justify-between text-[11px] bg-white/60 px-2 py-1 rounded">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-slate-700">{nomClient}</p>
                  <p className="text-slate-400">{heureIn} → {heureOut}</p>
                </div>
                <span className="text-slate-700 font-medium ml-2">{(c.heures_nettes || 0).toFixed(1)}h</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// Composant pour afficher les pointages d'une période de paie
function PeriodPunches({ employeeId, debut, fin, numero }) {
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId || !debut || !fin) return;
    axios.get(`${API_URL}/api/punch/employee/${employeeId}?limit=100`).then(r => r.status === 200 ? r.data : { punches: [] })
      .then(d => {
        // Filtrer les punches de cette période ET seulement les valides
        const filtered = (d.punches || []).filter(p => {
          const pDate = (p.punch_in_time || '').substring(0, 10);
          const isInPeriod = pDate >= debut && pDate <= fin;
          const isValid = ['valide', 'complete', 'modification_soumise'].includes(p.statut);
          return isInPeriod && isValid;
        });
        setPunches(filtered);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [employeeId, debut, fin]);

  if (loading) return <p className="text-xs text-slate-400 py-2 pl-4">Chargement...</p>;
  if (punches.length === 0) return <p className="text-xs text-slate-400 py-2 pl-4 italic">Aucun pointage pour cette période.</p>;

  // 🆕 Grouper les punchs lavage par (date, route) pour affichage propre
  const displayItems = groupLavagePunches(punches);

  return (
    <div className="ml-4 mt-1 mb-2 space-y-1 border-l-2 border-blue-200 pl-3">
      <p className="text-xs font-semibold text-slate-500 mb-1">Pointages P{numero} ({punches.length})</p>
      {displayItems.map(p => {
        if (p.__lavage_group) {
          return <LavageJourRow key={p.id} group={p} compact />;
        }
        if (p.type_punch === 'ferie_cnesst') {
          return (
            <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-yellow-100 last:border-0 bg-yellow-50/50 -mx-1 px-1 rounded">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-yellow-800">
                  {new Date(p.punch_in_time).toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
                <Badge className="bg-yellow-200 text-yellow-900 text-[9px] px-1.5 py-0">Férié</Badge>
                <span className="text-yellow-700 text-[11px]">{p.nom_ferie}</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-yellow-700">{(p.indemnite_montant || 0).toFixed(2)} $</span>
                {p.heures_ferie > 0 && (
                  <span className="text-[10px] text-yellow-600 ml-1">({(p.heures_ferie || 0).toFixed(1)}h)</span>
                )}
              </div>
            </div>
          );
        }
        const pause = p.heures_pause || 0;
        const heuresCash = (p.paiements_cash || []).reduce((s, pc) => s + (pc.heures || 0), 0);
        const montantCash = (p.paiements_cash || []).reduce((s, pc) => s + (pc.montant || 0), 0);
        return (
          <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
            <div>
              <span className="font-medium text-slate-700">
                {new Date(p.punch_in_time || p.date).toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
              <span className="text-slate-400 ml-2">
                {p.punch_in_time ? new Date(p.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—'}
                {' → '}
                {p.punch_out_time ? new Date(p.punch_out_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—'}
              </span>
              {pause > 0 && <span className="text-slate-300 ml-1">· P:{(pause * 60).toFixed(0)}m</span>}
              {/* 🆕 2026-05-11 — Affichage cash sur le punch */}
              {heuresCash > 0 && (
                <span className="ml-2 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                  💵 {heuresCash.toFixed(1)}h cash = {montantCash.toFixed(2)}$
                </span>
              )}
              {/* 🆕 [2026-06-11] Tag discret hors zone — informatif uniquement */}
              <span className="ml-2"><HorsZoneTag punch={p} position="any" /></span>
            </div>
            <span className="font-semibold text-blue-700">{(p.heures_nettes || 0).toFixed(1)}h</span>
          </div>
        );
      })}
      <div className="flex justify-between text-xs pt-1 font-bold text-blue-800">
        <span>Total</span>
        <span>{punches.reduce((s, p) => s + (p.heures_nettes || 0), 0).toFixed(1)}h</span>
      </div>
      {/* 🚚 [Phase v13.52 — 2026-07-20] Récap déplacement + total rémunéré si présent */}
      {(() => {
        const totalDeplMin = punches.reduce((s, p) => s + (parseInt(p.temps_deplacement_minutes, 10) || 0), 0);
        if (totalDeplMin <= 0) return null;
        const totalDeplH = totalDeplMin / 60;
        const totalNetH = punches.reduce((s, p) => s + (p.heures_nettes || 0), 0);
        return (
          <div className="mt-1 pt-1 border-t border-slate-200 text-[11px] space-y-0.5" data-testid="mon-espace-recap-deplacement">
            <div className="flex justify-between text-sky-700">
              <span>🚚 Temps déplacement</span>
              <span className="font-semibold">{totalDeplMin} min ({totalDeplH.toFixed(1)}h)</span>
            </div>
            <div className="flex justify-between text-emerald-800 font-bold">
              <span>Total rémunéré</span>
              <span>{(totalNetH + totalDeplH).toFixed(1)}h</span>
            </div>
          </div>
        );
      })()}
      {/* 🆕 2026-05-11 — Récap normal/cash de la semaine si applicable */}
      {(() => {
        const totalCashH = punches.reduce((s, p) => s + (p.paiements_cash || []).reduce((s2, pc) => s2 + (pc.heures || 0), 0), 0);
        const totalCashM = punches.reduce((s, p) => s + (p.paiements_cash || []).reduce((s2, pc) => s2 + (pc.montant || 0), 0), 0);
        const totalNormal = punches.reduce((s, p) => s + Math.max(0, (p.heures_nettes || 0) - (p.paiements_cash || []).reduce((s2, pc) => s2 + (pc.heures || 0), 0)), 0);
        return totalCashH > 0 && (
          <div className="mt-1 pt-1 border-t border-slate-200 text-[11px] space-y-0.5">
            <div className="flex justify-between text-slate-600"><span>Paie normale (légale)</span><span className="font-semibold">{totalNormal.toFixed(1)}h</span></div>
            <div className="flex justify-between text-emerald-700"><span>💵 Cash</span><span className="font-semibold">{totalCashH.toFixed(1)}h = {totalCashM.toFixed(2)}$</span></div>
          </div>
        );
      })()}
      {punches.some(p => p.type_punch === 'ferie_cnesst') && (
        <div className="flex justify-between text-xs font-bold text-yellow-700">
          <span>+ Indemnités fériés</span>
          <span>{punches.filter(p => p.type_punch === 'ferie_cnesst').reduce((s, p) => s + (p.indemnite_montant || 0), 0).toFixed(2)} $</span>
        </div>
      )}
    </div>
  );
}


/**
 * ✅ Alerte refus de punches — visible UNIQUEMENT dans MonEspace de l'employé concerné.
 * L'employé voit le refus + la note du valideur + un bouton "Compris" pour accuser réception.
 */
function RefusedPunchesAlert({ employeeId }) {
  const [punches, setPunches] = useState([]);
  const [accusing, setAccusing] = useState(null);

  const loadRefused = async () => {
    if (!employeeId) return;
    try {
      const r = await axios.get(`${API_URL}/api/punch/my-refused-pending/${employeeId}?days=30`);
      setPunches(r.data?.punches || []);
    } catch { setPunches([]); }
  };

  useEffect(() => { loadRefused(); }, [employeeId]);

  const handleAccuser = async (punchId) => {
    setAccusing(punchId);
    try {
      await axios.put(`${API_URL}/api/punch/${punchId}/accuser-refus`);
      setPunches(prev => prev.filter(p => p.id !== punchId));
    } catch { /* silent */ }
    finally { setAccusing(null); }
  };

  if (punches.length === 0) return null;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-CA', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 space-y-3 mb-4" data-testid="refused-punches-alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold text-red-900">
            {punches.length === 1 ? '1 pointage refusé' : `${punches.length} pointages refusés`}
          </h3>
          <p className="text-xs text-red-700 mt-0.5">
            Ces heures n'ont pas été comptées dans votre paie. Consultez la note du propriétaire ci-dessous.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {punches.map(p => (
          <div key={p.id} className="bg-white border border-red-200 rounded-lg p-3" data-testid={`refused-item-${p.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-slate-800">
                  Pointage du {fmtDate(p.punch_in_time)}
                </p>
                {p.numero_bt && <p className="text-xs text-slate-500">BT : {p.numero_bt}</p>}
                {(p.heures_nettes > 0) && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {p.heures_nettes.toFixed(2)}h — <span className="text-red-600 font-medium">non comptées</span>
                  </p>
                )}
              </div>
              <button
                onClick={() => handleAccuser(p.id)}
                disabled={accusing === p.id}
                className="text-xs bg-slate-700 hover:bg-slate-800 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                data-testid={`btn-accuser-${p.id}`}
              >
                {accusing === p.id ? '...' : 'Compris'}
              </button>
            </div>
            {p.notes_validation && (
              <div className="mt-2 pt-2 border-t border-red-100">
                <p className="text-[11px] text-slate-500 mb-0.5">Raison du refus :</p>
                <p className="text-xs text-slate-700 italic">« {p.notes_validation} »</p>
              </div>
            )}
            {!p.notes_validation && (
              <p className="text-[11px] text-slate-400 italic mt-2 pt-2 border-t border-red-100">
                Aucune raison fournie — contactez le propriétaire pour plus de détails.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentPunches({ employeeId }) {
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteInput, setNoteInput] = useState({});
  const [savingNote, setSavingNote] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    // 🐛 2026-05-12 — Bug : limit=10 puis filtrage (fériés/refusés/non-valides) + groupage lavage
    //   pouvait laisser <7 punches affichables. On passe à limit=30 pour garantir 7 items après filtre.
    axios.get(`${API_URL}/api/punch/employee/${employeeId}?limit=30`).then(r => r.status === 200 ? r.data : { punches: [] })
      .then(d => setPunches(d.punches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [employeeId]);

  const handleAddNote = async (punchId) => {
    const note = noteInput[punchId]?.trim();
    if (!note) return;
    setSavingNote(punchId);
    try {
      const r = await axios.put(`${API_URL}/api/punch/${punchId}/note-employe`, { note });
      if (r.status === 200) {
        const data = r.data;
        // Update local state with new status
        setPunches(prev => prev.map(p => p.id === punchId ? { 
          ...p, 
          note_employe: note, 
          modification_soumise: true,
          statut: p.statut === 'valide' ? 'modification_soumise' : p.statut 
        } : p));
        setNoteInput(prev => ({ ...prev, [punchId]: '' }));
        toast.success(data.message || 'Modification soumise');
      } else toast.error('Erreur');
    } catch { toast.error('Erreur réseau'); }
    finally { setSavingNote(null); }
  };

  const getStatutBadge = (punch) => {
    if (punch.statut === 'en_cours') return <Badge className="bg-blue-100 text-blue-700 text-[10px]">En cours</Badge>;
    if (punch.statut === 'modification_soumise') return <Badge className="bg-orange-100 text-orange-700 text-[10px]">Modification soumise</Badge>;
    if (punch.statut === 'valide') return <Badge className="bg-green-100 text-green-700 text-[10px]">Approuvé</Badge>;
    if (punch.statut === 'refuse') return <Badge className="bg-red-100 text-red-700 text-[10px]">Refusé</Badge>;
    if (punch.statut === 'complete') return <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">En attente d'approbation</Badge>;
    return null;
  };

  if (loading) return <div className="text-center py-4 text-sm text-slate-400">Chargement...</div>;
  // ✅ P1: Exclure jours fériés CNESST ET punches refusés de "Mes Pointages Récents"
  const punchesAffichables = punches.filter(p =>
    p.type_punch !== 'ferie_cnesst'
    && p.statut !== 'refuse'
    && p.valide !== false
  );
  if (punchesAffichables.length === 0) return <p className="text-sm text-slate-400 italic">Aucun pointage récent.</p>;

  // 🆕 Grouper les punchs lavage par (date, route) pour l'affichage récent
  const displayItems = groupLavagePunches(punchesAffichables);

  return (
    <div className="space-y-2">
      {displayItems.slice(0, 14).map(p => {
        // 🧽 Groupe lavage — affichage agrégé d'une journée de route
        if (p.__lavage_group) {
          return <LavageJourRow key={p.id} group={p} />;
        }
        // ── Jour férié CNESST — rendu spécifique ──
        if (p.type_punch === 'ferie_cnesst') {
          const date = new Date(p.punch_in_time || p.date);
          return (
            <div key={p.id} className="border border-yellow-300 bg-yellow-50 rounded-lg p-3" data-testid={`mon-espace-ferie-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800">
                      {date.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                    <Badge className="bg-yellow-200 text-yellow-900 text-[10px]">Jour férié</Badge>
                    {p.flag_admissibilite === 'a_verifier' && (
                      <Badge className="bg-orange-100 text-orange-800 text-[10px]" title={p.motif_admissibilite}>⚠️ À vérifier</Badge>
                    )}
                  </div>
                  <p className="text-xs text-yellow-800 mt-0.5">
                    {p.nom_ferie} · CNESST 1/20 ({p.nb_semaines_reference || 0} sem.)
                    {p.heures_ferie > 0 && <span className="ml-1">· {(p.heures_ferie || 0).toFixed(1)}h équiv.</span>}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-yellow-700">{(p.indemnite_montant || 0).toFixed(2)} $</span>
                  <p className="text-[10px] text-yellow-600">indemnité</p>
                </div>
              </div>
            </div>
          );
        }
        const date = new Date(p.punch_in_time || p.date);
        const heures = (p.heures_nettes || 0).toFixed(1);
        const heuresBrutes = (p.heures_travaillees || 0).toFixed(1);
        const pause = p.heures_pause || 0;
        const canSubmitModification = p.statut !== 'en_cours' && p.statut !== 'modification_soumise' && p.statut !== 'refuse';
        return (
          <div key={p.id} className={`border rounded-lg p-3 space-y-2 ${p.statut === 'modification_soumise' ? 'border-orange-300 bg-orange-50/30' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-800">
                    {date.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </p>
                  {getStatutBadge(p)}
                  {/* 🆕 [2026-06-11] Tags discrets hors zone (in / out séparés si applicables) */}
                  <HorsZoneTag punch={p} position="in" />
                  <HorsZoneTag punch={p} position="out" />
                </div>
                <p className="text-xs text-slate-500">
                  {p.punch_in_time ? new Date(p.punch_in_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  {' → '}
                  {p.punch_out_time ? new Date(p.punch_out_time).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : 'En cours'}
                  {pause > 0 && <span className="text-slate-400 ml-1">· Pause: {(pause * 60).toFixed(0)} min</span>}
                </p>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-blue-700">{heures}h</span>
                {pause > 0 && <p className="text-[10px] text-slate-400">{heuresBrutes}h brut</p>}
              </div>
            </div>
            {/* Note existante (affichage historique) */}
            {p.note_employe && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <p className="text-xs text-blue-800"><span className="font-semibold">Votre modification :</span> {p.note_employe}</p>
                {p.statut === 'modification_soumise' && (
                  <p className="text-[10px] text-orange-600 italic mt-0.5">En attente de traitement par le propriétaire</p>
                )}
                {p.statut === 'valide' && (
                  <p className="text-[10px] text-green-600 italic mt-0.5">Modification traitée et approuvée</p>
                )}
              </div>
            )}
            {/* Soumettre une modification (tant que le punch n'est pas en cours de validation) */}
            {canSubmitModification && (
              <div className="space-y-1">
                <p className="text-[10px] text-slate-400">
                  {p.note_employe ? 'Soumettre une nouvelle modification' : 'Soumettre une modification'}
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteInput[p.id] || ''}
                    onChange={e => setNoteInput(prev => ({ ...prev, [p.id]: e.target.value }))}
                    placeholder="Ex: J'ai oublié de me puncher à 8h..."
                    className="flex-1 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    data-testid={`note-input-${p.id}`}
                  />
                  <button
                    onClick={() => handleAddNote(p.id)}
                    disabled={!noteInput[p.id]?.trim() || savingNote === p.id}
                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50 hover:bg-blue-700"
                    data-testid={`note-submit-${p.id}`}
                  >
                    {savingNote === p.id ? '...' : 'Soumettre'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <p className="text-[10px] text-slate-400 text-center">Les 14 derniers pointages</p>
    </div>
  );
}



// ── Widget paie estimée sous-traitant (basée sur le rapport LMDS de la semaine) ──
function SousTraitantPaieWidget({ employeeId, token, taux }) {
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [semaineAffichee, setSemaineAffichee] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    // 🆕 2026-04-29 — Cherche le MEILLEUR rapport sur la fenêtre [N-5 → N] où N=semaine courante.
    // Priorité: semaine la plus récente qui a un total_installateur_net > 0 (avec lignes non-déléguées).
    // Fix: la version précédente ratait systématiquement la semaine courante car la boucle partait de N-1.
    const now = new Date();
    const oneJan = new Date(now.getFullYear(), 0, 1);
    const currentWeek = Math.ceil(((now - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);

    const fetchWeek = async (annee, sem) => {
      try {
        const r = await axios.get(`${API_URL}/api/lmds/rapports/${employeeId}/${annee}/${sem}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.status === 200 && r.data && Array.isArray(r.data.lignes) && r.data.lignes.length > 0) {
          return { data: r.data, sem, annee };
        }
      } catch { /* no-op */ }
      return null;
    };

    (async () => {
      // 🔄 2026-05-05 (Bug fix Eliot) — Logique simplifiée et correcte :
      //   On parcourt les semaines N → N-5 et on garde la plus récente
      //   (highest week number) qui est SOIT envoyée SOIT payée.
      //   Avant : un rapport "envoyé non payé" hors semaine courante perdait
      //   la priorité contre un rapport "payé" plus ancien — d'où S16 affiché
      //   alors que S17 venait d'être envoyé/sauvegardé.
      const annee = now.getFullYear();
      let best = null;  // { data, sem, annee, isPaye, isEnvoye }
      for (let offset = 0; offset <= 5; offset++) {
        let sem = currentWeek - offset;
        let a = annee;
        if (sem <= 0) { sem += 52; a--; }
        const found = await fetchWeek(a, sem);
        if (!found) continue;
        const totalInst = found.data?.total_installateur_net || 0;
        const hasOwnLines = (found.data?.lignes || []).some(l => !l.delegue_a_id);
        if (!hasOwnLines || totalInst <= 0.01) continue;
        const isPaye = !!(found.data?.paye);
        const isEnvoye = !!(found.data?.rapport_envoye || found.data?.envoye);
        if (!isPaye && !isEnvoye) continue;  // ni payé ni envoyé → on ignore
        // On garde la plus récente (boucle décroissante donc le 1er match gagne)
        if (!best) best = { ...found, isPaye, isEnvoye };
      }
      if (best) {
        setRapport(best.data);
        setSemaineAffichee(best.sem);
      }
      setLoading(false);
    })();
  }, [employeeId, token]);

  if (loading) return <Card><CardContent className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" /></CardContent></Card>;

  const lignes = (rapport?.lignes || []).filter(l => !l.est_delegation && !l.delegue_a_id);
  // Utiliser les totaux PRÉ-CALCULÉS du rapport (source de vérité)
  const totalInstallateur = rapport?.total_installateur_net || 0;
  const totalAvecTaxes = rapport?.total_avec_taxes || 0;
  const nbLignes = lignes.length;

  // 🔄 Phase B2 (Eliot, 2026-05-04) — Bascule basée sur l'ÉTAT du rapport,
  // PAS sur le jour de la semaine. Helper testable dans `lib/versementLabel.js`.
  const titreVersement = versementLabel(rapport);

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <DollarSign className="w-4 h-4 text-blue-500" /> {titreVersement} {semaineAffichee ? `— Semaine ${semaineAffichee}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {nbLignes === 0 ? (
          <p className="text-sm text-slate-400 italic">Aucun rapport disponible.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Avant taxes</span>
              <span className="text-lg font-bold text-slate-800">{totalInstallateur.toFixed(2)} $</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <span className="text-sm font-medium text-emerald-700">Avec taxes</span>
              <span className="text-lg font-bold text-emerald-700">{totalAvecTaxes.toFixed(2)} $</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// 🆕 2026-05-12 — Section "Mes Permis opérationnels" : affiche à l'employé la liste
//   des permis qu'il possède (Code d'accès bureau, Conduire unités, Opérer nacelles…)
//   et ceux qu'il n'a pas, pour qu'il sache ce qu'il a le droit de faire ou non.
//   Source des données :
//     - profil.permis_accordes (array {permis_id, permis_nom, accordee_le, accordee_par, notes})
//     - GET /api/permis-types (catalogue global)
function MesPermisOperationnelsSection({ permisAccordes }) {
  const [types, setTypes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/permis-types`);
        if (cancelled) return;
        setTypes(r.data?.types || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Erreur");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) return <p className="text-xs text-slate-500" data-testid="mes-permis-error">Impossible de charger les permis ({error}).</p>;
  if (!types) return <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Chargement…</div>;

  const accordesById = new Map((permisAccordes || []).map(p => [p.permis_id, p]));
  const accordes = types.filter(t => accordesById.has(t.id));
  const nonAccordes = types.filter(t => !accordesById.has(t.id));

  return (
    <div className="space-y-3" data-testid="mes-permis-content">
      <p className="text-xs text-slate-500">
        Ce que vous avez le droit de faire (ou non) pour l'entreprise. Pour toute demande, contactez votre administrateur.
      </p>

      {/* Permis accordés */}
      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">
          Accordés ({accordes.length})
        </p>
        {accordes.length === 0 && (
          <p className="text-xs text-slate-400 italic">Aucun permis accordé pour le moment.</p>
        )}
        {accordes.map(t => {
          const detail = accordesById.get(t.id);
          return (
            <div key={t.id}
              className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50"
              data-testid={`permis-accorde-${t.id}`}
            >
              <div className="flex items-start gap-2 min-w-0">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-900 truncate">{t.nom}</p>
                  {(detail?.accordee_le || detail?.accordee_par) && (
                    <p className="text-[11px] text-emerald-700">
                      Accordé{detail?.accordee_le ? ` le ${detail.accordee_le}` : ""}
                      {detail?.accordee_par ? ` par ${detail.accordee_par}` : ""}
                    </p>
                  )}
                  {detail?.notes && (
                    <p className="text-[11px] text-emerald-800 mt-0.5 italic">{detail.notes}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Permis non accordés */}
      {nonAccordes.length > 0 && (
        <div className="space-y-1.5 pt-2 border-t border-slate-200">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
            Non accordés ({nonAccordes.length})
          </p>
          {nonAccordes.map(t => (
            <div key={t.id}
              className="flex items-start gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50"
              data-testid={`permis-non-accorde-${t.id}`}
            >
              <span className="text-slate-400 mt-0.5 shrink-0">✕</span>
              <p className="text-sm text-slate-500 line-through decoration-slate-300">{t.nom}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const MonEspace = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const planningRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoursView, setHoursView] = useState("periode");
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [contractTemplate, setContractTemplate] = useState(null);
  const [showContractText, setShowContractText] = useState(false);
  const [signing, setSigning] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // 🆕 2026-04-29 — Diagnostic identité (detect mauvais lien user→employé)
  const [diagnostic, setDiagnostic] = useState(null);

  const isProprietaire = estProprietaire(user); // Cat H — identité Propriétaire : dispatch UI (Owner voit dropdown de sélection d'employé, autres voient leur propre espace).
  const { canDo } = usePermissions() || {};
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via matrice actions.
  const canVoirTableauBordTerrain    = !!(canDo && canDo('mon_espace_tableau_bord_terrain'));
  const canVoirBtsRenfort            = !!(canDo && canDo('mon_espace_bts_renfort'));
  const canVoirCalendrierProduction  = !!(canDo && canDo('mon_espace_calendrier_production'));
  // 🗺️ [v13.63 Phase 3 — 2026-02-28] Rechercher mon chantier (secours horaire)
  const canRechercherChantier        = !!(canDo && canDo('mon_espace_rechercher_chantier'));
  // 📏 [v13.63 Phase 4 — 2026-02-28] Kilométrage employé
  const canVoirKilometrage           = !!(canDo && canDo('mon_espace_kilometrage'));
  const [showRechercheChantier, setShowRechercheChantier] = useState(false);
  const [chantierRefreshKey, setChantierRefreshKey] = useState(0);

  // Scroll vers Planning si demandé via navigation state
  useEffect(() => {
    if (location.state?.scrollTo === 'planning' && planningRef.current && !loading) {
      setTimeout(() => planningRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.state, loading]);

  useEffect(() => {
    if (isProprietaire) {
      loadEmployeesList();
    } else if (user?.employee_id) {
      // Employé non-propriétaire avec employee_id lié → auto-chargement
      setEmployeeId(user.employee_id);
      setSelectedEmployee(`${user.prenom || ''} ${user.nom || ''}`.trim());
    } else {
      // Pas de employee_id → afficher un message
      setLoading(false);
    }
    // 🆕 Charger le diagnostic d'identité (détecte mauvais lien user→employé)
    if (user && !isProprietaire) {
      axios.get(`${API_URL}/api/auth/me/diagnostic`)
        .then(r => setDiagnostic(r.data))
        .catch(() => setDiagnostic(null));
    }
  }, [user]);

  useEffect(() => {
    if (employeeId) loadData(employeeId);
  }, [employeeId]);

  const loadEmployeesList = async () => {
    try {
      const res = await api.getEmployees();
      setEmployees(res.employees || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadData = async (empId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/mon-espace/${empId}`);
      if (res.status !== 200) throw new Error("Employé introuvable");
      const d = res.data;
      setData(d);

      // Charger le template si contrat non valide
      if (!d.contrat?.valide) {
        // Cat H — `role === 'installateur'` = catégorie métier (LMDS) déterminant quel template de contrat charger. Pas gate RBAC.
        const isInst = d.profil?.role_terrain === 'sous_traitant' || d.profil?.departement === 'LMDS' || aLeRole(user, 'installateur');
        const templateType = isInst ? 'sous_traitant_lmds' : '';
        const tplUrl = templateType ? `${API_URL}/api/contracts/template?type_template=${templateType}` : `${API_URL}/api/contracts/template`;
        const tplRes = await axios.get(tplUrl);
        const tplData = tplRes.data;
        setContractTemplate(tplData.template || null);
      }
    } catch (e) {
      toast.error("Erreur chargement de vos données");
    } finally {
      setLoading(false);
    }
  };

  const loadContractTemplate = async () => {
    try {
      // Cat H — `role === 'installateur'` = catégorie métier (LMDS) pour choisir le bon template. Pas gate RBAC.
      const isInst = aLeRole(user, 'installateur');
      const tplUrl = isInst 
        ? `${API_URL}/api/contracts/template?type_template=sous_traitant_lmds`
        : `${API_URL}/api/contracts/template`;
      const tplRes = await axios.get(tplUrl);
      const tplData = tplRes.data;
      setContractTemplate(tplData.template || null);
      setShowContractText(true);
    } catch (e) {
      toast.error("Erreur chargement du contrat");
    }
  };


  const handleSign = async (signatureData, signatureNom) => {
    setSigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/contracts/${employeeId}/sign`, { employee_id: employeeId, signature_data: signatureData, signature_nom: signatureNom });
      if (res.status === 200) {
        toast.success("✅ Contrat signé avec succès !");
        setShowSignModal(false);
        loadData(employeeId);
      } else {
        const err = res.data;
        toast.error(err.detail || "Erreur lors de la signature");
      }
    } catch (e) { toast.error("Erreur lors de la signature"); }
    finally { setSigning(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !employeeId) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG ou WebP.');
      return;
    }
    setUploadingPhoto(true);
    try {
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      const compressed = await compressImageIfNeeded(file);
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await axios.post(`${API_URL}/api/employees/${employeeId}/upload-photo-profil`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.status !== 200) throw new Error('Erreur upload');
      toast.success('Photo de profil mise à jour !');
      loadData(employeeId);
    } catch (err) {
      console.error('Erreur upload photo profil:', err);
      toast.error('Erreur lors du téléversement de la photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!employeeId) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer la photo de profil ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/employees/${employeeId}/delete-photo-profil`);
      if (res.status !== 200) throw new Error();
      toast.success('Photo supprimée.');
      loadData(employeeId);
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  // Helper: heures selon la vue sélectionnée
  const getHoursForView = () => {
    if (!data) return { heures: 0, label: "", debut: "", fin: "" };
    const h = data.heures;
    if (hoursView === "semaine") {
      return { heures: h.semaine_courante, label: `Semaine du ${new Date(h.semaine_debut).toLocaleDateString('fr-CA')}`, debut: h.semaine_debut, fin: "" };
    } else if (hoursView === "mois") {
      return { heures: h.mois_courant, label: h.mois_label, debut: "", fin: "" };
    } else {
      const pp = h.periodes_paie?.find(p => p.est_courante);
      return { heures: pp?.heures || 0, label: `Période ${pp?.numero || ''}: ${pp?.debut || ''} → ${pp?.fin || ''}`, debut: pp?.debut || "", fin: pp?.fin || "" };
    }
  };

  // Affichage: sélecteur d'employé pour le propriétaire
  if (isProprietaire && !employeeId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mon Espace</h1>
          <p className="text-slate-500 text-sm">Sélectionnez un employé pour consulter son espace</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3 max-w-md">
              {employees.map(e => (
                <div key={e.id}
                  className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => { setEmployeeId(e.id); setSelectedEmployee(`${e.prenom} ${e.nom}`); }}>
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar photoUrl={e.photo_url} prenom={e.prenom || ''} nom={e.nom || ''} size="md" />
                    <div>
                      <p className="font-medium">{e.prenom} {e.nom}</p>
                      <p className="text-xs text-slate-500">{e.type_employe} — {e.departement}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  if (!data && !isProprietaire && !loading) return (
    <div className="text-center py-20">
      <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <p className="text-slate-600 font-medium">Votre compte n'est pas encore lié à une fiche employé.</p>
      <p className="text-slate-400 text-sm mt-1">Demandez à votre administrateur de lier votre compte via <strong>Configuration → Comptes & Accès</strong>.</p>
    </div>
  );

  // Guard: si proprietaire a sélectionné un employé mais data pas encore chargé
  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  const { profil, heures, paie, vetements, contrat, avertissements, conges, bons_assignes, historique_paie } = data;
  const hoursData = getHoursForView();
  const photoMissing = !profil?.photo_url;
  // Cat H — classification métier (sous-traitant LMDS) pour dispatch UI (contrat spécifique, badges). Pas gate RBAC.
  const isSousTraitant = profil?.role_terrain === 'sous_traitant' || profil?.departement === 'LMDS' || aLeRole(user, 'installateur');

  // ═══════════════════════════════════════════════════════════════════════════
  // ── Composant : Bannière diagnostic identité (commune) ─────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  const DiagnosticBanner = () => {
    if (!diagnostic || diagnostic.ok) return null;
    const hasCritical = diagnostic.issues.some(i => i.severity === 'critical');
    const bgColor = hasCritical ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300';
    const txtColor = hasCritical ? 'text-red-800' : 'text-amber-800';
    const iconColor = hasCritical ? 'text-red-600' : 'text-amber-600';
    return (
      <Card className={`${bgColor} border`} data-testid="banniere-diagnostic-identite">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${iconColor}`} />
            <div className="flex-1">
              <p className={`font-semibold ${txtColor}`}>
                {hasCritical ? '🚨 Problème d\'identité détecté' : '⚠️ Vérification d\'identité'}
              </p>
              <ul className={`text-sm mt-1.5 space-y-1 ${txtColor}`}>
                {diagnostic.issues.map((iss, i) => <li key={i}>• {iss.message}</li>)}
              </ul>
              {diagnostic.auto_candidate && (
                <p className="text-xs text-slate-600 mt-2">
                  Un employé avec votre email a été trouvé : <b>{diagnostic.auto_candidate.prenom} {diagnostic.auto_candidate.nom}</b> ({diagnostic.auto_candidate.email}). Déconnectez-vous puis reconnectez-vous pour ré-associer automatiquement votre compte.
                </p>
              )}
              {!diagnostic.auto_candidate && hasCritical && (
                <p className="text-xs text-slate-600 mt-2">
                  Contactez l'administrateur pour corriger le lien de votre compte dans <b>Configuration → Comptes & Accès</b>.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ── Mon Espace SOUS-TRAITANT (vue simplifiée) ─────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  if (isSousTraitant && !isProprietaire) {
    return (
      <div className="space-y-6">
        <DiagnosticBanner />
        {/* ── Profil + Photo ── */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-5">
              <div className="relative group">
                <EmployeeAvatar photoUrl={profil.photo_url} prenom={profil.prenom || ''} nom={profil.nom || ''} size="xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">{profil.prenom} {profil.nom}</h2>
                <p className="text-sm text-slate-500 mt-0.5">Sous-traitant — Le Marché du Store</p>
                {profil.telephone && <p className="text-xs text-slate-400 mt-1">{profil.telephone}</p>}
                {profil.email && <p className="text-xs text-slate-400">{profil.email}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Paie estimée — Rapport de la semaine ── */}
        <SousTraitantPaieWidget employeeId={employeeId} token={token} taux={profil.lmds_taux || 0.65} />

        {/* ── Articles sous quotas (toujours visible pour sous-traitant) ── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Package className="w-4 h-4 text-slate-500" /> Mes articles sous quotas</CardTitle>
          </CardHeader>
          <CardContent>
            {vetements?.articles?.length > 0 ? (
              <div className="space-y-1.5">
                {vetements.articles.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0 ${a.regle_active === false ? 'opacity-60' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-slate-700">{a.modele}</span>
                        {a.taille && <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{a.taille}</span>}
                        {a.obligatoire && <span className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Obligatoire</span>}
                        {a.regle_active && a.regle_nom && (
                          <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            {a.regle_nom}{a.periode_label ? ` — ${a.periode_label}` : ''}
                          </span>
                        )}
                        {a.regle_active === false && (
                          <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                            Aucun quota actif
                          </span>
                        )}
                      </div>
                      {a.prochaine_remise && a.regle_active && (
                        <p className="text-[10px] text-slate-400 mt-0.5">🔄 Prochaine remise : {a.prochaine_remise}</p>
                      )}
                    </div>
                    <div className="text-right ml-2 shrink-0">
                      <span className={`font-bold ${a.disponible > 0 ? 'text-purple-600' : 'text-red-500'}`}>{a.disponible}</span>
                      <span className="text-xs text-slate-400 ml-1">/ {a.quota_annuel}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">Aucun article attribué pour le moment.</p>
            )}
          </CardContent>
        </Card>

        {/* ── Mes congés (identique aux autres employés, respecte la config visible) ── */}
        {conges && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Umbrella className="w-4 h-4 text-blue-500" /> Mes Congés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { key: "vacances", label: "Vacances", color: "text-blue-600" },
                    { key: "maladie", label: "Maladie", color: "text-red-600" },
                    { key: "personnel", label: "Personnel", color: "text-purple-600" },
                  ].filter(({ key }) => {
                    // Respecter la config visible de la fiche employé
                    const cfg = profil?.conges_config?.[key];
                    return cfg?.visible !== false; // visible par défaut si pas de config
                  }).map(({ key, label, color }) => (
                    <div key={key} className="bg-slate-50 rounded p-2">
                      <p className="text-xs text-slate-500">{label}</p>
                      {(() => {
                        const total = conges.solde?.[`${key}_total`] ?? 0;
                        const restant = conges.solde?.[`${key}_restant`] ?? total;
                        const utilise = Math.max(0, total - restant);
                        const cfg = profil?.conges_config?.[key];
                        const isUnlimited = cfg?.aucune_limite === true;
                        return (
                          <>
                            <p className={`text-xl font-bold ${color}`}>
                              {isUnlimited ? '∞' : utilise.toFixed(0)}
                              <span className="text-xs font-normal text-slate-400"> / {isUnlimited ? '∞' : `${total} j pris`}</span>
                            </p>
                            {!isUnlimited && (
                              <p className={`text-xs font-semibold mt-0.5 ${restant <= 0 ? 'text-red-500' : 'text-slate-500'}`}>
                                {restant.toFixed(0)} j restants
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
                {conges.nb_en_attente > 0 && (
                  <p className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                    {conges.nb_en_attente} demande{conges.nb_en_attente > 1 ? 's' : ''} en attente d'approbation
                  </p>
                )}
                <a href="/conges" className="block w-full">
                  <button className="w-full text-xs text-blue-600 border border-blue-200 rounded py-1.5 hover:bg-blue-50 transition-colors">
                    <Plus className="w-3 h-3 inline mr-1" /> Faire une demande de congé
                  </button>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Mon Planning (congés approuvés + événements) ── */}
        <Card>
          <CardContent className="py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Mon Planning</p>
                  <p className="text-xs text-slate-500">Congés approuvés et événements</p>
                </div>
              </div>
              <a href="/mon-planning" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                Voir le calendrier
              </a>
            </div>
          </CardContent>
        </Card>

        {/* ── Mon contrat ── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><FileSignature className="w-4 h-4 text-slate-500" /> Mon contrat</CardTitle>
          </CardHeader>
          <CardContent>
            {contrat?.signe ? (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-green-800">Contrat signé</p>
                  {contrat?.derniere_signature?.date_signature && <p className="text-xs text-green-600">Signé le {new Date(contrat.derniere_signature.date_signature).toLocaleDateString('fr-CA')}</p>}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <p className="text-sm font-medium text-amber-800">Contrat non signé</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadContractTemplate}>
                    Consulter le contrat
                  </Button>
                  <Button size="sm" className="bg-blue-600" onClick={loadContractTemplate}>
                    <FileSignature className="w-4 h-4 mr-1" /> Signer mon contrat
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 🆕 [Phase 102 — 2026-07-09] Historique complet des contrats signés */}
        <MesContratsSection />

        {/* Contract modals */}
        <Dialog open={showContractText} onOpenChange={setShowContractText}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{contractTemplate?.titre || 'Contrat de travail'}</DialogTitle></DialogHeader>
            <pre className="mt-4 whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed p-4 bg-slate-50 rounded-lg">
              {contractTemplate?.content || 'Aucun contenu disponible.'}
            </pre>
            <div className="flex justify-end mt-4 gap-2">
              <Button variant="outline" onClick={() => setShowContractText(false)}>Fermer</Button>
              <Button className="bg-blue-600" onClick={() => { setShowContractText(false); setShowSignModal(true); }}>Signer</Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl mx-auto">
            <DialogHeader><DialogTitle>Signer le contrat</DialogTitle></DialogHeader>
            <div className="mt-2 overflow-x-auto">
              <p className="text-sm text-slate-600 mb-4">
                En signant, vous confirmez avoir lu et accepté les termes du contrat.{" "}
                <button className="text-blue-600 underline text-sm" onClick={() => { setShowSignModal(false); setShowContractText(true); }}>Consulter le contrat</button>
              </p>
              <SignatureCanvas onSave={handleSign} onCancel={() => setShowSignModal(false)} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 🆕 Bannière diagnostic identité (commune à tous les non-propriétaires) */}
      <DiagnosticBanner />
      {/* ── Widget de pointage unifié (terrain uniquement, mobile + desktop) ── */}
      {/* ChantierDuJour gère tous les cas: BT standard, BT interne, pause, fallback. */}
      {!isProprietaire && (
        <ChantierDuJour key={chantierRefreshKey} />
      )}

      {/* 🗺️ [v13.63 Phase 3 — 2026-02-28] Rechercher mon chantier — solution
          de secours si l'employé a été oublié à la cédule du jour. */}
      {!isProprietaire && canRechercherChantier && (
        <div className="flex justify-center" data-testid="rechercher-chantier-cta-wrapper">
          <button
            type="button"
            onClick={() => setShowRechercheChantier(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-orange-300 bg-white hover:bg-orange-50 text-orange-700 text-xs font-medium transition-colors"
            data-testid="btn-rechercher-chantier"
          >
            <span className="text-base leading-none">🔎</span>
            Je ne vois pas mon chantier — chercher manuellement
          </button>
        </div>
      )}

      {showRechercheChantier && (
        <RechercherMonChantier
          open={showRechercheChantier}
          onOpenChange={setShowRechercheChantier}
          token={token}
          onAfterSelect={async () => { setChantierRefreshKey((k) => k + 1); }}
        />
      )}

      {/* 📏 [v13.63 Phase 4 — 2026-02-28] Mon kilométrage du jour (RBAC) */}
      {!isProprietaire && canVoirKilometrage && (
        <MonKilometrage token={token} />
      )}

      {/* 📅 [v13.63 Phase 5 — 2026-02-28] Récap kilométrage 7 derniers jours */}
      {!isProprietaire && canVoirKilometrage && (
        <MonKilometrageSemaine token={token} />
      )}

      {/* Bannière photo manquante */}
      {photoMissing && !isProprietaire && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3" data-testid="photo-reminder-banner">
          <span className="text-2xl">📷</span>
          <div className="flex-1">
            <p className="text-amber-800 font-semibold text-sm">Votre photo de profil est manquante</p>
            <p className="text-amber-700 text-xs">Ajoutez votre photo dans la section "Profil & Photo" ci-dessous pour compléter votre dossier.</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {isProprietaire && (
            <Button variant="ghost" size="sm" onClick={() => { setEmployeeId(null); setData(null); }}>
              ← Retour
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Mon Espace</h1>
            <p className="text-slate-500 text-sm">{selectedEmployee || `${profil.prenom} ${profil.nom}`}</p>
          </div>
        </div>
        {!contrat.valide && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            <AlertTriangle className="w-4 h-4 mr-1" /> Contrat à signer
          </Badge>
        )}
      </div>

      {/* Alerte contrat non signé — intégrée dans la section Mon Contrat ci-dessous */}

      {/* ✅ Alerte punches refusés — visible UNIQUEMENT par l'employé concerné */}
      <RefusedPunchesAlert employeeId={profil.id} />

      {/* Layout 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ═══ COLONNE GAUCHE : Profil & Infos ═══ */}
        <div className="space-y-3">

          {/* 1. Profil & Photo */}
          <AccordionSection icon={User} title="Mon Profil" badge={`${profil.prenom} ${profil.nom}`} defaultOpen={true}>
            <div className="flex items-start gap-4">
              {/* Photo avec bouton changement */}
              <div className="relative group shrink-0">
                <EmployeeAvatar photoUrl={profil.photo_url} prenom={profil.prenom || ''} nom={profil.nom || ''} size="xl" />
                {/* Overlay changement photo */}
                <label
                  htmlFor="photo-upload-input"
                  className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  title="Changer la photo"
                >
                  {uploadingPhoto
                    ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  }
                </label>
                <input
                  id="photo-upload-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="photo-upload-input"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl font-bold text-slate-900">{profil.prenom} {profil.nom}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  <Badge className="bg-blue-100 text-blue-700 text-xs">{profil.departement}</Badge>
                  <Badge variant={profil.permanence === 'saisonnier' ? 'outline' : 'default'} className="text-xs">
                    {profil.permanence === 'saisonnier' ? 'Saisonnier' : 'Permanent'}
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-600 text-xs capitalize">
                    {profil.role_terrain === 'chef_equipe' ? "Chef d'équipe" : profil.role_terrain === 'peintre' ? 'Peintre' : profil.type_employe}
                  </Badge>
                </div>
                {/* Informations personnelles */}
                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  {profil.email && (
                    <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-slate-400" />{profil.email}</div>
                  )}
                  {profil.telephone && (
                    <a href={`tel:${profil.telephone}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-800"><Phone className="w-3 h-3" />{profil.telephone}</a>
                  )}
                  {profil.date_embauche && (
                    <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-slate-400" />Depuis le {new Date(profil.date_embauche).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  )}
                  {profil.numero_employe && (
                    <div className="flex items-center gap-2"><User className="w-3 h-3 text-slate-400" />ID: {profil.numero_employe}</div>
                  )}
                </div>
                {/* Boutons photo */}
                <div className="flex gap-2 mt-2">
                  <label htmlFor="photo-upload-input" className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer underline underline-offset-2">
                    {profil.photo_url ? 'Changer la photo' : 'Ajouter une photo'}
                  </label>
                  {profil.photo_url && (
                    <button onClick={handlePhotoDelete} className="text-xs text-red-400 hover:text-red-600 underline underline-offset-2" data-testid="btn-delete-photo">
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </AccordionSection>

          {/* 🏠 [v13.63 Phase 4] Adresse domicile self-service (own-data = Cat H).
              Affichée uniquement pour les employés éligibles au kilométrage. */}
          {!isProprietaire && canVoirKilometrage && employeeId && (
            <MonAdresseDomicile
              employeeId={employeeId}
              token={token}
              onSaved={() => setChantierRefreshKey((k) => k + 1)}
            />
          )}

          {/* 2. TableauBordTerrain — RBAC `mon_espace_tableau_bord_terrain` (Vague 5b) */}
          {!isProprietaire && canVoirTableauBordTerrain && (
            <TableauBordTerrain token={token} role={user.role} />
          )}

          {/* 🆕 2b. BTs en renfort — RBAC `mon_espace_bts_renfort` (Vague 5b).
              Composant 100% isolé, ne touche pas à TableauBordTerrain. */}
          {canVoirBtsRenfort && (
            <BTsEnRenfort token={token} role={user.role} />
          )}

          {/* 2b. Mon Calendrier de Production — RBAC `mon_espace_calendrier_production` (Vague 5b) */}
          {!isProprietaire && canVoirCalendrierProduction && (
            <Card data-testid="card-calendrier-production-haut">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Mon Calendrier de Production</p>
                      <p className="text-xs text-slate-500">Vos chantiers et horaires planifiés</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/mon-planning')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-voir-calendrier-production-haut"
                  >
                    Voir le calendrier
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 3. Mes Articles sous Quotas */}
          <AccordionSection icon={Package} title="Mes Articles sous Quotas" badge={vetements.articles?.length > 0 ? `${vetements.articles.length} article(s)` : undefined}>
            <div className="space-y-2">
              {vetements.articles && vetements.articles.length > 0 ? (
                <>
                  {vetements.articles.map((article, i) => (
                    <div key={i} className={`flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0 ${article.regle_active === false ? 'opacity-60' : ''}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800">{article.modele}</span>
                          {article.taille && <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{article.taille}</span>}
                          {article.obligatoire && <span className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Obligatoire</span>}
                          {/* 🆕 2026-05-23 — Badge période/règle active */}
                          {article.regle_active && article.regle_nom && (
                            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                              {article.regle_nom}{article.periode_label ? ` — ${article.periode_label}` : ''}
                            </span>
                          )}
                          {article.regle_active === false && (
                            <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                              Aucun quota actif pour cette période
                            </span>
                          )}
                        </div>
                        {article.prochaine_remise && article.regle_active && (
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            🔄 Prochaine remise à zéro : <span className="font-medium text-slate-500">{article.prochaine_remise}</span>
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-2 shrink-0">
                        <span className={`font-bold text-lg ${article.disponible > 0 ? 'text-purple-600' : 'text-red-500'}`}>
                          {article.disponible}
                        </span>
                        <span className="text-xs text-slate-400 ml-1">/ {article.quota_annuel} dispo</span>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-sm text-slate-400 text-center py-2">Aucun article sous quota configuré</p>
              )}
            </div>
          </AccordionSection>

          {/* 4. Mes Congés */}
          {conges && (
            <AccordionSection icon={Umbrella} title="Mes Congés">
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { key: "vacances", label: "Vacances", color: "text-blue-600" },
                    { key: "maladie", label: "Maladie", color: "text-red-600" },
                    { key: "personnel", label: "Personnel", color: "text-purple-600" },
                  ].filter(({ key }) => {
                    const cfg = profil?.conges_config?.[key];
                    return cfg?.visible !== false;
                  }).map(({ key, label, color }) => {
                    const cfg = profil?.conges_config?.[key];
                    const isUnlimited = cfg?.aucune_limite === true;
                    const total = conges.solde?.[`${key}_total`] ?? 0;
                    const restant = conges.solde?.[`${key}_restant`] ?? total;
                    return (
                    <div key={key} className="bg-slate-50 rounded p-2">
                      <p className="text-xs text-slate-500">{label}</p>
                      <p className={`text-xl font-bold ${color}`}>
                        {isUnlimited ? '∞' : Math.max(0, total - restant).toFixed(0)}
                        <span className="text-xs font-normal text-slate-400"> / {isUnlimited ? '∞' : `${total} j pris`}</span>
                      </p>
                      {!isUnlimited && (
                        <p className={`text-xs font-semibold mt-0.5 ${restant <= 0 ? 'text-red-500' : 'text-slate-500'}`}>
                          {restant.toFixed(0)} j restants
                        </p>
                      )}
                    </div>
                    );
                  })}
                </div>
                {conges.nb_en_attente > 0 && (
                  <p className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                    ⏳ {conges.nb_en_attente} demande{conges.nb_en_attente > 1 ? 's' : ''} en attente d'approbation
                  </p>
                )}
                <a href="/conges" className="block w-full">
                  <button className="w-full text-xs text-blue-600 border border-blue-200 rounded py-1.5 hover:bg-blue-50 transition-colors">
                    <Plus className="w-3 h-3 inline mr-1" /> Faire une demande de congé
                  </button>
                </a>
              </div>
            </AccordionSection>
          )}

          {/* 5. Mon Contrat */}
          <AccordionSection
            icon={FileSignature}
            title="Mon Contrat"
            badge={contrat.valide ? 'Valide' : 'À signer'}
            headerAction={!contrat.valide && contractTemplate ? (
              <Button
                size="sm"
                className="text-xs h-7 px-2 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => setShowSignModal(true)}
                data-testid="btn-signer-contrat-header"
              >
                Signer
              </Button>
            ) : null}
          >
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {contrat.valide
                  ? <><CheckCircle className="w-5 h-5 text-green-500" /><span className="text-green-700 font-medium">Contrat valide</span></>
                  : <><AlertTriangle className="w-5 h-5 text-orange-500" /><span className="text-orange-700 font-medium">Signature requise</span></>}
              </div>
              {contrat.derniere_signature && (
                <p className="text-xs text-slate-500">
                  Dernière signature: {new Date(contrat.derniere_signature.date_signature).toLocaleDateString('fr-CA')}
                  {" "} par {contrat.derniere_signature.signature_nom}
                </p>
              )}
              <p className="text-xs text-slate-500">
                Prochaine expiration: {new Date(contrat.date_expiration).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              {!contrat.valide && contractTemplate && (
                <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600" onClick={() => setShowSignModal(true)}>
                  Signer le contrat
                </Button>
              )}
            </div>
          </AccordionSection>

          {/* 🆕 [Phase 102 — 2026-07-09] Historique complet — Mes contrats signés */}
          <MesContratsSection />

          {/* 📝 [Phase 103 — 2026-07-09] Historique des évaluations */}
          <MesEvaluationsSection />

          {/* 6. Mes Avertissements */}
          <AccordionSection icon={AlertTriangle} title="Mes Avertissements" badge={avertissements.total > 0 ? `${avertissements.total} actif(s)` : 'Aucun'}>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className={`text-3xl font-bold ${avertissements.total > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {avertissements.total}
                </span>
                <span className="text-slate-500">avertissement{avertissements.total > 1 ? 's' : ''}</span>
              </div>
              {avertissements.liste.length > 0 && (
                <div className="space-y-1 mt-2">
                  {avertissements.liste.map((a, i) => (
                    <div key={i} className="text-xs text-slate-600 bg-red-50 p-2 rounded">
                      <span className="font-medium">{a.type_avertissement}</span> — {a.date?.slice(0, 10)}
                    </div>
                  ))}
                </div>
              )}
              {avertissements.total === 0 && (
                <p className="text-xs text-green-600">Aucun avertissement actif 👍</p>
              )}
            </div>
          </AccordionSection>

        </div>

        {/* ═══ COLONNE DROITE : Activité & Paie ═══ */}
        <div className="space-y-3">

          {/* 1. Mes Pointages Récents */}
          <AccordionSection icon={Clock} title="Mes Pointages Récents" defaultOpen={true}>
            <RecentPunches employeeId={profil.id} />
          </AccordionSection>

          {/* 2. Ma Paie Estimée */}
          <AccordionSection icon={DollarSign} title="Ma Paie Estimée" badge={paie.periode?.numero ? `Période ${paie.periode.numero}` : undefined} defaultOpen={true}>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Période {paie.periode?.numero}: {paie.periode?.debut} → {paie.periode?.fin}</p>
                {paie.is_paie_simple ? (
                  <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-4 text-center" data-testid="paie-simple-summary">
                    <p className="text-xs text-green-600 mb-0.5">Paie (argent, sans déductions)</p>
                    <p className="text-2xl font-bold text-green-700">{(paie.paie_nette_estimee || 0).toFixed(2)} $</p>
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-400 mb-0.5">Brut</p>
                      <p className="text-xl font-bold text-slate-800">{(paie.paie_estimee_brute || 0).toFixed(2)} $</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-green-600 mb-0.5">Net estimé</p>
                      <p className="text-xl font-bold text-green-700">{(paie.paie_nette_estimee || 0).toFixed(2)} $</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-500">
                  <span>Taux horaire</span>
                  <span className="font-medium text-slate-700">{(paie.taux_horaire || 0).toFixed(2)} $/h</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Heures période</span>
                  <span className="font-medium text-slate-700">{(paie.heures_periode_courante || 0).toFixed(1)} h</span>
                </div>

                {/* Déductions — masquées si l'employé est payé simplement en argent */}
                {!paie.is_paie_simple && (
                <div className="border-t pt-2 mt-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Déductions</p>
                  <div className="space-y-1">
                    {(paie.rrq_employe || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">RRQ (employé)</span>
                        <span className="text-red-500">-{Math.abs(paie.rrq_employe || 0).toFixed(2)} $</span>
                      </div>
                    )}
                    {(paie.ae_employe || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">AE (employé)</span>
                        <span className="text-red-500">-{Math.abs(paie.ae_employe || 0).toFixed(2)} $</span>
                      </div>
                    )}
                    {(paie.rqap_employe || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">RQAP (employé)</span>
                        <span className="text-red-500">-{Math.abs(paie.rqap_employe || 0).toFixed(2)} $</span>
                      </div>
                    )}
                    {(paie.assurances_employe_periode || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Assurances collectives</span>
                        <span className="text-red-500">-{Math.abs(paie.assurances_employe_periode || 0).toFixed(2)} $</span>
                      </div>
                    )}
                    {(paie.total_cotisations || 0) > 0 && (
                      <div className="flex justify-between border-t pt-1 font-medium">
                        <span className="text-slate-600">Sous-total déductions</span>
                        <span className="text-red-600">-{Math.abs(paie.total_cotisations || 0).toFixed(2)} $</span>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Bandeau informatif si paie simple (argent sans déductions) */}
                {paie.is_paie_simple && (
                  <div className="border-t pt-2 mt-1" data-testid="paie-simple-banner">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 flex items-start gap-2">
                      <span className="text-lg leading-none">💵</span>
                      <div className="text-xs text-emerald-800 leading-relaxed">
                        <p className="font-semibold">Payé en argent — sans déductions</p>
                        <p className="text-emerald-700">Aucune cotisation (RRQ, AE, RQAP) ni impôt n&apos;est prélevé.</p>
                      </div>
                    </div>
                  </div>
                )}

                {!paie.is_paie_simple && (
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span className="text-slate-700">Net (avant impôt)</span>
                  <span className="text-slate-800">{(paie.paie_nette_avant_impot || 0).toFixed(2)} $</span>
                </div>
                )}

                {/* Impôt estimé — masqué si l'employé est payé simplement en argent */}
                {!paie.is_paie_simple && (
                <div className="border-t pt-2 bg-amber-50 rounded-lg p-2 -mx-1">
                  <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Impôt estimé *</p>
                  <div className="space-y-0.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-700">Impôt fédéral</span>
                      <span className="text-amber-700">{(paie.impot_federal_estime || 0) > 0 ? `-${(paie.impot_federal_estime).toFixed(2)} $` : "0.00 $"}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-700">Impôt Québec</span>
                      <span className="text-amber-700">{(paie.impot_qc_estime || 0) > 0 ? `-${(paie.impot_qc_estime).toFixed(2)} $` : "0.00 $"}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm font-bold mt-1.5 pt-1 border-t border-amber-200">
                    <span className="text-green-700">Paie nette estimée</span>
                    <span className="text-green-700">{(paie.paie_nette_estimee || 0).toFixed(2)} $</span>
                  </div>
                </div>
                )}

                {/* Version simplifiée quand paie en argent : juste la paie nette sans mention d'impôt */}
                {paie.is_paie_simple && (
                  <div className="border-t pt-2 bg-green-50 rounded-lg p-2 -mx-1">
                    <div className="flex justify-between text-sm font-bold">
                      <span className="text-green-700">Paie nette estimée</span>
                      <span className="text-green-700">{(paie.paie_nette_estimee || 0).toFixed(2)} $</span>
                    </div>
                  </div>
                )}

                {!paie.is_paie_simple && (
                <p className="text-[10px] text-slate-400 italic leading-relaxed">
                  * Estimation basée sur les paliers d&apos;imposition 2026 (fédéral + QC). Ne tient pas compte de vos autres revenus, crédits personnels, REER ni de votre situation fiscale particulière. Votre impôt réel peut différer.
                </p>
                )}

                {/* Prime horaire */}
                {(paie.prime_horaire || 0) > 0 && (
                  <div className="flex justify-between text-sm border-t pt-2 mt-1">
                    <span className="text-amber-600 flex items-center gap-1.5">
                      <Gift className="w-3.5 h-3.5" /> Prime ({(paie.prime_horaire || 0).toFixed(2)} $/h)
                    </span>
                    <span className="font-bold text-amber-600">+{(paie.prime_estimee_periode || 0).toFixed(2)} $</span>
                  </div>
                )}
                {(paie.prime_horaire || 0) > 0 && (
                  <p className="text-[10px] text-slate-400 pl-5">Versée sans déductions</p>
                )}
              </div>
            </div>

            {/* Bonus saisonnier */}
            {paie.bonus && (
              <div className="mt-3 pt-3 border-t border-blue-100">
                <p className="text-xs font-semibold text-blue-700 flex items-center gap-1.5 mb-2">
                  <Star className="w-3.5 h-3.5" /> Mon Bonus Saisonnier
                </p>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-slate-500">
                      {paie.bonus.type === 'fixe' ? 'Bonus fixe — payable en fin de saison' : 'Accumulé depuis le début de la saison'}
                    </p>
                    <p className="text-3xl font-bold text-blue-700 mt-1">{(paie.bonus.bonus_accumule || 0).toFixed(2)} $</p>
                    <p className="text-xs text-slate-400">payable en fin de saison selon les performances</p>
                  </div>
                  <div className="border-t pt-2 space-y-1">
                    {paie.bonus.type === 'horaire' ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Taux bonus</span>
                          <span className="font-medium">{paie.bonus.taux_bonus.toFixed(2)} $/h</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Heures saison</span>
                          <span className="font-medium">{paie.bonus.heures_saison.toFixed(1)} h</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Type</span>
                        <span className="font-medium">Montant fixe</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </AccordionSection>

          {/* 3. Mes Heures Travaillées */}
          <AccordionSection icon={Clock} title="Mes Heures Travaillées" badge={`${hoursData.heures.toFixed(1)} h`} defaultOpen={true}>
            <div className="flex items-end gap-2 mb-3">
              <span className="text-4xl font-bold text-blue-600">{hoursData.heures.toFixed(1)}</span>
              <span className="text-slate-500 mb-1">heures</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">{hoursData.label}</p>
            <div className="flex gap-1 mb-3">
              {Object.entries(VIEW_LABELS).map(([key, label]) => (
                <Button key={key} size="sm" variant={hoursView === key ? 'default' : 'outline'}
                  className="text-xs h-7 px-2" onClick={() => setHoursView(key)}>
                  {key === 'periode' ? 'Période' : label}
                </Button>
              ))}
            </div>

            {/* Dernières périodes */}
            {hoursView === "periode" && heures.periodes_paie && (
              <div className="space-y-1">
                <p className="text-xs text-slate-500 font-medium mb-2">Toutes les périodes de l'année — cliquer pour voir les pointages</p>
                {heures.periodes_paie.slice().reverse().map(pp => (
                  <div key={pp.numero}>
                    <div
                      className={`flex items-center justify-between py-1.5 px-2 rounded text-sm cursor-pointer ${
                        pp.est_courante ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'
                      } ${expandedPeriod === pp.numero ? 'ring-1 ring-blue-300' : ''}`}
                      onClick={() => setExpandedPeriod(expandedPeriod === pp.numero ? null : pp.numero)}
                    >
                      <span className="text-slate-600 min-w-0 mr-2">
                        <span className="font-medium">P{pp.numero}:</span>{' '}
                        <span className="text-xs">{pp.debut?.slice(5)} → {pp.fin?.slice(5)}</span>
                        {pp.est_courante && <Badge className="ml-1.5 text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">Courante</Badge>}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-medium">{pp.heures.toFixed(1)} h</span>
                        <span className="text-xs text-slate-400">{pp.nombre_punches || 0} punch{(pp.nombre_punches || 0) > 1 ? 'es' : ''}</span>
                      </div>
                    </div>
                    {expandedPeriod === pp.numero && (
                      <PeriodPunches employeeId={profil.id} debut={pp.debut} fin={pp.fin} numero={pp.numero} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Historique des paies — toutes les périodes de l'année (v13.37) */}
            {historique_paie && historique_paie.length > 0 && (paie.taux_horaire > 0 || historique_paie.some(p => p.heures > 0)) && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-3">
                  <BarChart2 className="w-3.5 h-3.5 text-green-600" /> Historique des paies — toutes les périodes de l'année
                </p>
                {/* Barres de comparaison visuelles */}
                {(() => {
                  const maxNet = Math.max(...historique_paie.map(p => p.paie_nette_estimee || 0), 1);
                  return (
                    <div className="space-y-1 mb-4">
                      {[...historique_paie].reverse().map((pp) => {
                        const pct = maxNet > 0 ? Math.round((pp.paie_nette_estimee || 0) / maxNet * 100) : 0;
                        return (
                          <div key={pp.numero} className="flex items-center gap-3">
                            <span className={`text-xs w-14 text-right font-mono shrink-0 ${pp.est_courante ? 'font-bold text-blue-600' : 'text-slate-500'}`}>
                              P{pp.numero}
                            </span>
                            <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden relative">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${pp.est_courante ? 'bg-blue-400' : 'bg-green-400'}`}
                                style={{ width: `${pct}%` }}
                              />
                              {pct > 15 && (
                                <span className="absolute inset-0 flex items-center pl-2 text-[10px] font-semibold text-white">
                                  {(pp.paie_nette_estimee || 0).toFixed(0)} $
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-slate-400 w-20 shrink-0 text-right">
                              {pp.heures.toFixed(1)} h
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Tableau détaillé */}
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs min-w-[500px]">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-2 px-2 text-slate-400 font-medium">Période</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium">Heures</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium">Prime</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium" title="Primes / bonus saisonniers payés en argent comptant (transactions cash manuelles).">Bonus 💵</th>
                        <th className="text-right py-2 px-2 text-emerald-700 font-medium" title="Heures du punch payées directement en argent comptant (sans déduction). Calculé depuis les paiements cash des pointages.">Paie cash 💵</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium">Brut</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium">Déductions</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium">Impôts *</th>
                        <th className="text-right py-2 px-2 text-slate-400 font-medium">Net est.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...historique_paie].reverse().map((pp) => (
                        <tr
                          key={pp.numero}
                          className={`border-b border-slate-50 ${pp.est_courante ? 'bg-blue-50 font-semibold' : 'hover:bg-slate-50'}`}
                        >
                          <td className="py-2 px-2">
                            <span className={`${pp.est_courante ? 'text-blue-700' : 'text-slate-600'}`}>
                              P{pp.numero}
                            </span>
                            {pp.est_courante && (
                              <Badge className="ml-1.5 text-[9px] px-1 py-0 bg-blue-100 text-blue-700 hover:bg-blue-100">En cours</Badge>
                            )}
                            <span className="block text-[10px] text-slate-400 font-normal">
                              {pp.debut?.slice(5)} → {pp.fin?.slice(5)}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-slate-600">{pp.heures.toFixed(1)}</td>
                          <td
                            className="py-2 px-2 text-right text-amber-600 font-medium"
                            title={(() => {
                              // 🆕 [Phase v13.37 — 2026-07-13] Tooltip col Prime : globale + TOUTES les primes ponctuelles (cash + chèque)
                              const primeGlobale = pp.prime_globale_total ?? Math.max(0, (pp.prime_estimee || 0) - (pp.prime_ponctuelle_total || 0));
                              const primePonc = pp.prime_ponctuelle_total || 0;
                              const lignes = [];
                              if (primeGlobale > 0) lignes.push(`Prime globale contrat : ${primeGlobale.toFixed(2)} $`);
                              if (primePonc > 0) {
                                lignes.push(`Primes ponctuelles : ${primePonc.toFixed(2)} $`);
                                (pp.prime_ponctuelle_detail || []).forEach(d => {
                                  const modeInfo = d.mode_paiement === 'cash' ? '💵 cash' : `🧾 chèque${d.est_declaree ? ' [déclarée]' : ' [non déclarée]'}`;
                                  lignes.push(`  • ${d.date} : ${d.heures}h × ${d.prime_horaire}$ = ${d.montant}$${d.description ? ' (' + d.description + ')' : ''} — ${modeInfo}`);
                                });
                              }
                              return lignes.length ? lignes.join('\n') : 'Aucune prime cette période.';
                            })()}
                            data-testid={`prime-${pp.numero}`}
                          >
                            {(pp.prime_estimee || 0) > 0 ? `+${(pp.prime_estimee).toFixed(2)} $` : '—'}
                            {(pp.prime_ponctuelle_total || 0) > 0 && (
                              <span className="block text-[9px] text-amber-500 font-normal" data-testid={`prime-ponctuelle-badge-${pp.numero}`}>
                                dont +{(pp.prime_ponctuelle_total).toFixed(2)}$ ponctuelle
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right text-emerald-600 font-medium" title={pp.bonus_cash_detail?.map(b => `${b.date} ${b.description}: ${b.montant}$`).join('\n') || 'Primes / bonus saisonniers cash'}>
                            {(pp.bonus_cash || 0) > 0 ? `+${(pp.bonus_cash).toFixed(2)} $` : '—'}
                          </td>
                          <td
                            className="py-2 px-2 text-right text-emerald-700 font-semibold"
                            title={(pp.paie_cash_detail && pp.paie_cash_detail.length)
                              ? pp.paie_cash_detail.map(d => {
                                  // 🆕 Phase 105 — Décomposition cash direct + prime cash si présente
                                  const primeInfo = (d.prime_horaire || 0) > 0 && d.prime_mode_paiement === 'cash'
                                    ? ` (+ prime ${d.prime_horaire}$/h = ${d.prime_montant}$)`
                                    : '';
                                  return `${d.date} : ${d.heures}h × ${d.taux}$ = ${d.montant}$${primeInfo}`;
                                }).join('\n')
                              : 'Aucune heure payée en argent comptant cette période.'}
                            data-testid={`paie-cash-${pp.numero}`}
                          >
                            {(pp.paie_cash_total || 0) > 0
                              ? `+${(pp.paie_cash_total).toFixed(2)} $`
                              : '—'}
                            {(pp.paie_cash_heures || 0) > 0 && (
                              <span className="block text-[9px] text-emerald-500 font-normal">
                                {(pp.paie_cash_heures).toFixed(1)}h
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right text-slate-700">{(pp.paie_brute || 0).toFixed(2)} $</td>
                          <td className="py-2 px-2 text-right text-red-500">
                            {(pp.cotisations_total || 0) > 0 ? `-${(pp.cotisations_total).toFixed(2)} $` : '—'}
                          </td>
                          <td className="py-2 px-2 text-right text-amber-600">
                            {(pp.impot_total || 0) > 0 ? `-${(pp.impot_total).toFixed(2)} $` : '—'}
                          </td>
                          <td className={`py-2 px-2 text-right font-bold ${pp.est_courante ? 'text-blue-700' : 'text-green-600'}`}>
                            {(pp.paie_nette_estimee || 0).toFixed(2)} $
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 bg-slate-50">
                        <td className="py-2 px-2 text-slate-500 font-medium">Total ({historique_paie.length} période{historique_paie.length > 1 ? 's' : ''})</td>
                        <td className="py-2 px-2 text-right font-medium text-slate-700">
                          {historique_paie.reduce((s, p) => s + (p.heures || 0), 0).toFixed(1)} h
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-amber-600">
                          {historique_paie.reduce((s, p) => s + (p.prime_estimee || 0), 0) > 0
                            ? `+${historique_paie.reduce((s, p) => s + (p.prime_estimee || 0), 0).toFixed(2)} $`
                            : '—'}
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-emerald-600">
                          {historique_paie.reduce((s, p) => s + (p.bonus_cash || 0), 0) > 0
                            ? `+${historique_paie.reduce((s, p) => s + (p.bonus_cash || 0), 0).toFixed(2)} $`
                            : '—'}
                        </td>
                        <td className="py-2 px-2 text-right font-bold text-emerald-700">
                          {historique_paie.reduce((s, p) => s + (p.paie_cash_total || 0), 0) > 0
                            ? `+${historique_paie.reduce((s, p) => s + (p.paie_cash_total || 0), 0).toFixed(2)} $`
                            : '—'}
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-slate-700">
                          {historique_paie.reduce((s, p) => s + (p.paie_brute || 0), 0).toFixed(2)} $
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-red-500">
                          {historique_paie.reduce((s, p) => s + (p.cotisations_total || 0), 0) > 0
                            ? `-${historique_paie.reduce((s, p) => s + (p.cotisations_total || 0), 0).toFixed(2)} $`
                            : '—'}
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-amber-600">
                          {historique_paie.reduce((s, p) => s + (p.impot_total || 0), 0) > 0
                            ? `-${historique_paie.reduce((s, p) => s + (p.impot_total || 0), 0).toFixed(2)} $`
                            : '—'}
                        </td>
                        <td className="py-2 px-2 text-right font-bold text-green-700">
                          {historique_paie.reduce((s, p) => s + (p.paie_nette_estimee || 0), 0).toFixed(2)} $
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-[10px] text-slate-400 italic mt-2">* Estimations basées sur les paliers d'imposition 2026. Votre impôt réel peut différer.</p>
              </div>
            )}
          </AccordionSection>

          {/* 4. Calendrier de production — CTA déplacé en haut (juste sous les chantiers) pour visibilité */}

          {/* 🆕 2026-05-12 — Mes Permis opérationnels : ce que l'employé a le droit de faire pour l'entreprise.
              Source : profil.permis_accordes (provient de la fiche Employé → onglet « Permis »). */}
          <AccordionSection
            icon={ShieldCheck}
            title="Mes Permis opérationnels"
            badge={(profil.permis_accordes && profil.permis_accordes.length > 0)
              ? `${profil.permis_accordes.length} accordé${profil.permis_accordes.length > 1 ? 's' : ''}`
              : 'Aucun'}
          >
            <MesPermisOperationnelsSection permisAccordes={profil.permis_accordes || []} />
          </AccordionSection>

          {/* 🔐 Connexion P4 — Mes appareils connectés (uniquement MON espace : jamais les appareils d'autrui ici) */}
          {(!user?.employee_id || profil?.id === user?.employee_id) && (
          <AccordionSection icon={MonitorSmartphone} title="Mes appareils connectés">
            <div data-testid="section-mes-appareils">
              <p className="text-xs text-slate-500 mb-2">Chaque appareil sur lequel vous êtes connecté. Déconnectez ceux que vous ne reconnaissez pas.</p>
              <ListeAppareils />
            </div>
          </AccordionSection>
          )}

          {/* 5. Accès plateforme de paie */}
          {profil.code_acces_plateforme_paie && (
            <AccordionSection icon={Key} title="Mon Accès — Plateforme de Paie">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Nom d'utilisateur (Employeur D)</p>
                <p className="font-mono font-semibold text-slate-800 text-sm tracking-wide">{profil.code_acces_plateforme_paie}</p>
              </div>
              <p className="text-xs text-slate-400 mt-2">Votre nom d'utilisateur pour accéder à la plateforme de paie. Communiquez avec votre employeur pour obtenir votre mot de passe.</p>
            </AccordionSection>
          )}

        </div>
      </div>

      {/* Modal: Lire le contrat */}
      <Dialog open={showContractText} onOpenChange={setShowContractText}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{contractTemplate?.titre || 'Contrat de travail'}</DialogTitle></DialogHeader>
          <pre className="mt-4 whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed p-4 bg-slate-50 rounded-lg">
            {contractTemplate?.content || 'Aucun contenu disponible.'}
          </pre>
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" onClick={() => setShowContractText(false)}>Fermer</Button>
            <Button className="bg-blue-600" onClick={() => { setShowContractText(false); setShowSignModal(true); }}>
              Signer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Signature */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Signer le contrat de travail</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <p className="text-sm text-slate-600 mb-4">
              En signant, vous confirmez avoir lu et accepté les termes du contrat de travail.
              {" "}<button className="text-blue-600 underline text-sm" onClick={() => { setShowSignModal(false); setShowContractText(true); }}>Consulter le contrat</button>
            </p>
            <SignatureCanvas
              onSave={handleSign}
              onCancel={() => setShowSignModal(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
